import { useEffect } from 'react';

/**
 * SEOHead - Reusable SEO meta tags component using native DOM manipulation
 * Compatible with React 19
 *
 * Usage:
 * - Default (homepage): <SEOHead />
 * - Custom page: <SEOHead title="Articles" description="Browse patient stories" />
 * - Article page: <SEOHead title={article.title} description={article.hook} type="article" />
 */
export const SEOHead = ({
  title,
  description,
  url,
  image,
  type = 'website',
  author,
  publishedDate,
  modifiedDate,
  section,
  noIndex = false
}) => {
  // Default values for Real Patients
  const defaults = {
    siteName: 'Real Patients',
    title: 'Real Patients | Real Voices Transforming Healthcare',
    description: 'Real Patients connects verified patient voice with the teams transforming care — through a decade of community, research, and on-demand intelligence.',
    url: 'https://www.realpatients.com',
    image: 'https://www.realpatients.com/og-image-v3.png',
    twitterHandle: '@realpatients'
  };

  // Build full title
  const fullTitle = title
    ? `${title} | ${defaults.siteName}`
    : defaults.title;

  // Use provided values or defaults
  const metaDescription = description || defaults.description;
  const metaUrl = url || defaults.url;
  const metaImage = image || defaults.image;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to set or create meta tag
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Helper to set or create link tag
    const setLink = (rel, href) => {
      let tag = document.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };

    // Primary Meta Tags
    setMeta('title', fullTitle);
    setMeta('description', metaDescription);

    // Canonical URL
    setLink('canonical', metaUrl);

    // Robots
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph / Facebook
    setMeta('og:type', type, true);
    setMeta('og:url', metaUrl, true);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', metaDescription, true);
    setMeta('og:image', metaImage, true);
    setMeta('og:site_name', defaults.siteName, true);

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:url', metaUrl);
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', metaDescription);
    setMeta('twitter:image', metaImage);
    setMeta('twitter:site', defaults.twitterHandle);

    // Article-specific meta tags
    if (type === 'article') {
      if (author) setMeta('article:author', author, true);
      if (publishedDate) setMeta('article:published_time', publishedDate, true);
      if (modifiedDate) setMeta('article:modified_time', modifiedDate, true);
      if (section) setMeta('article:section', section, true);
    }

    // Additional SEO tags
    setMeta('author', 'Real Patients');
    setMeta('publisher', 'Real Patients');
    setMeta('theme-color', '#f15b41');

    // Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Real Patients",
      "url": "https://www.realpatients.com",
      "logo": "https://www.realpatients.com/real-patients-logo.png",
      "description": "Real Patients connects verified patient voice with the teams transforming care — through a decade of community, research, and on-demand intelligence.",
      "sameAs": [
        "https://www.linkedin.com/company/realpatients",
        "https://twitter.com/realpatients"
      ],
      "founder": {
        "@type": "Person",
        "name": "Jen Horonjeff, PhD",
        "jobTitle": "Co-founder & Chief Patient Officer",
        "url": "https://www.linkedin.com/in/jenhoronjeff/"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "hello@realpatients.io"
      }
    };

    // Add or update schema script
    let schemaScript = document.querySelector('script[data-schema="organization"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-schema', 'organization');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(organizationSchema);

  }, [fullTitle, metaDescription, metaUrl, metaImage, type, author, publishedDate, modifiedDate, section, noIndex, defaults.siteName, defaults.twitterHandle]);

  // This component doesn't render anything visible
  return null;
};

export default SEOHead;
